/**
 * Chart of accounts, presets, and scenarios for the Accounting Cycle simulator.
 * Supports both chapter-embedded mode (PRESETS) and standalone/embedded mode
 * (DEMO_SCENARIO used on the landing page and chapters without a specific scenario).
 */

export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

export type Account = { id: string; name: string; type: AccountType };

export const ACCOUNTS: Account[] = [
  { id: 'cash',         name: 'Cash',                  type: 'asset'     },
  { id: 'bank',         name: 'Bank',                  type: 'asset'     },
  { id: 'capital',      name: 'Capital',               type: 'equity'    },
  { id: 'drawings',     name: 'Drawings',              type: 'equity'    },
  { id: 'sales',        name: 'Sales',                 type: 'revenue'   },
  { id: 'purchases',    name: 'Purchases',             type: 'expense'   },
  { id: 'rent-exp',     name: 'Rent Expense',          type: 'expense'   },
  { id: 'salaries-exp', name: 'Salaries Expense',      type: 'expense'   },
  { id: 'debtors',      name: 'Debtors (Receivables)', type: 'asset'     },
  { id: 'creditors',    name: 'Creditors (Payables)',  type: 'liability' },
  { id: 'furniture',    name: 'Furniture',             type: 'asset'     },
  { id: 'machinery',    name: 'Machinery',             type: 'asset'     },
];

export function accountById(id: string): Account | undefined {
  return ACCOUNTS.find((a) => a.id === id);
}

/**
 * Rules of debit & credit — used for inline learner hints and trial balance logic.
 * Returns whether a debit *increases* this account.
 */
export function debitIncreases(type: AccountType): boolean {
  return type === 'asset' || type === 'expense';
}

/** Quick-pick presets for chapter-embedded mode. */
export const PRESETS = [
  { id: 'capital-intro',  label: 'Owner brings in ₹1,00,000 cash as capital',  description: 'Started business with ₹1,00,000 cash',  debit: 'cash',      credit: 'capital', amount: 100000 },
  { id: 'goods-cash',     label: 'Goods purchased for ₹20,000 in cash',         description: 'Goods purchased for cash',              debit: 'purchases', credit: 'cash',    amount: 20000  },
  { id: 'sales-credit',   label: 'Sold goods worth ₹15,000 to Ravi on credit',  description: 'Sold goods on credit to Ravi',          debit: 'debtors',  credit: 'sales',   amount: 15000  },
  { id: 'rent-bank',      label: 'Paid rent ₹4,000 by cheque',                  description: 'Rent paid by cheque',                   debit: 'rent-exp', credit: 'bank',    amount: 4000   },
];

/**
 * Demo scenario — used on the landing page and as the default when no
 * chapter-specific scenario applies. Four transactions for a tiny shop.
 * Debit totals = Credit totals = ₹1,30,000 (trial balance balances).
 *
 * Verification:
 *   Cash     Dr 1,00,000 | Capital Cr 1,00,000  → Cash asset Dr, Capital equity Cr
 *   Purchases Dr 30,000  | Cash    Cr  30,000    → Purchases expense Dr, Cash asset Cr
 *   Cash     Dr 50,000   | Sales   Cr  50,000    → Cash asset Dr, Sales revenue Cr
 *   Rent Exp Dr  8,000   | Cash    Cr   8,000    → Rent expense Dr, Cash asset Cr
 *
 * Ledger balances:
 *   Cash:     1,00,000 − 30,000 + 50,000 − 8,000 = 1,12,000 Dr (asset, Dr-natural)
 *   Capital:  1,00,000 Cr (equity, Cr-natural)
 *   Purchases:   30,000 Dr (expense, Dr-natural)
 *   Sales:       50,000 Cr (revenue, Cr-natural)
 *   Rent Exp:     8,000 Dr (expense, Dr-natural)
 *
 * Trial Balance:
 *   Debit column:  Cash 1,12,000 + Purchases 30,000 + Rent Exp 8,000 = 1,50,000
 *   Credit column: Capital 1,00,000 + Sales 50,000                   = 1,50,000  ✓
 */
export type ScenarioEntry = {
  id: string;
  description: string;
  debit: string;
  credit: string;
  amount: number;
};

export const DEMO_SCENARIO: ScenarioEntry[] = [
  { id: 'demo-1', description: 'Started shop with ₹1,00,000 cash',    debit: 'cash',      credit: 'capital', amount: 100000 },
  { id: 'demo-2', description: 'Bought goods for ₹30,000 in cash',    debit: 'purchases', credit: 'cash',    amount: 30000  },
  { id: 'demo-3', description: 'Sold goods for ₹50,000 in cash',      debit: 'cash',      credit: 'sales',   amount: 50000  },
  { id: 'demo-4', description: 'Paid shop rent ₹8,000 in cash',       debit: 'rent-exp',  credit: 'cash',    amount: 8000   },
];
